import { normalizePlaygroundLanguage } from './playgroundLanguages';
import { formatLanguageDisplay } from '../../utils/roadmapDisplay';

export const PLAYGROUND_SNIPPETS_STORAGE_SUFFIX = '_playground_snippets';
export const PLAYGROUND_ACTIVE_SNIPPET_SUFFIX = '_playground_active_snippet';

export function playgroundSnippetsStorageKey(userId) {
  return `${userId}${PLAYGROUND_SNIPPETS_STORAGE_SUFFIX}`;
}

export function playgroundActiveSnippetStorageKey(userId) {
  return `${userId}${PLAYGROUND_ACTIVE_SNIPPET_SUFFIX}`;
}

function createSnippetId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `snippet_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export const PLAYGROUND_UNTITLED_SNIPPET_TITLE = 'Untitled';

export function normalizeSnippetTitle(title, fallback = PLAYGROUND_UNTITLED_SNIPPET_TITLE) {
  const trimmed = (title || '').trim().replace(/\s+/g, ' ');
  if (!trimmed) return fallback;
  return trimmed.slice(0, 80);
}

export function suggestSnippetTitle(code, language, existingTitles = []) {
  const trimmed = (code || '').trim();
  const langLabel = formatLanguageDisplay(language);
  const titles = new Set(
    (existingTitles || []).map((title) => normalizeSnippetTitle(title).toLowerCase())
  );

  if (!trimmed) {
    return nextUntitledName(langLabel, titles);
  }

  const patterns = [
    /(?:function|def|fn)\s+([a-zA-Z_$][\w$]*)/,
    /(?:class|struct|enum|interface|trait|type)\s+([a-zA-Z_$][\w$]*)/,
    /(?:const|let|var)\s+([a-zA-Z_$][\w$]*)\s*=/,
    /([a-zA-Z_$][\w$]*)\s*=\s*(?:function|\(|async\s)/,
    /def\s+([a-zA-Z_$][\w$]*)\s*\(/,
  ];

  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match?.[1] && !isReservedName(match[1])) {
      return normalizeSnippetTitle(match[1]);
    }
  }

  return nextUntitledName(langLabel, titles);
}

function isReservedName(name) {
  const reserved = new Set([
    'if', 'else', 'for', 'while', 'return', 'import', 'export', 'from',
    'const', 'let', 'var', 'function', 'class', 'new', 'this', 'true', 'false',
  ]);
  return reserved.has(name.toLowerCase());
}

function nextUntitledName(langLabel, titles) {
  const base = `${langLabel} snippet`;
  let index = 1;
  while (titles.has(`${base} ${index}`.toLowerCase())) {
    index += 1;
  }
  return `${base} ${index}`;
}

/** @deprecated use suggestSnippetTitle */
export function deriveSnippetTitle(code, language) {
  return suggestSnippetTitle(code, language);
}

export function getSnippetCodePreview(code, maxLength = 72) {
  const trimmed = (code || '').trim();
  if (!trimmed) return 'Empty snippet';

  const firstLine = trimmed.split('\n').find((line) => line.trim()) || '';
  const compact = firstLine.trim();
  if (!compact) return 'Empty snippet';
  if (compact.length <= maxLength) return compact;
  return `${compact.slice(0, maxLength - 1)}…`;
}

export function formatSnippetIndexDate(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

export function readPlaygroundSnippets(userId) {
  if (!userId || typeof localStorage === 'undefined') return [];

  try {
    const raw = localStorage.getItem(playgroundSnippetsStorageKey(userId));
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item) => item && typeof item.id === 'string')
      .map((item) => ({
        id: item.id,
        title: normalizeSnippetTitle(item.title),
        language: normalizePlaygroundLanguage(item.language),
        code: typeof item.code === 'string' ? item.code : '',
        createdAt: item.createdAt || item.updatedAt || Date.now(),
        updatedAt: item.updatedAt || item.createdAt || Date.now(),
      }))
      .sort((a, b) => b.updatedAt - a.updatedAt);
  } catch {
    return [];
  }
}

export function writePlaygroundSnippets(userId, snippets) {
  if (!userId || typeof localStorage === 'undefined') return;
  localStorage.setItem(playgroundSnippetsStorageKey(userId), JSON.stringify(snippets));
}

export function readActiveSnippetId(userId) {
  if (!userId || typeof localStorage === 'undefined') return null;
  return localStorage.getItem(playgroundActiveSnippetStorageKey(userId)) || null;
}

export function writeActiveSnippetId(userId, snippetId) {
  if (!userId || typeof localStorage === 'undefined') return;
  if (!snippetId) {
    localStorage.removeItem(playgroundActiveSnippetStorageKey(userId));
    return;
  }
  localStorage.setItem(playgroundActiveSnippetStorageKey(userId), snippetId);
}

export function createPlaygroundSnippet({ title, language, code }) {
  const now = Date.now();
  const normalizedLanguage = normalizePlaygroundLanguage(language);

  return {
    id: createSnippetId(),
    title: normalizeSnippetTitle(title),
    language: normalizedLanguage,
    code: code || '',
    createdAt: now,
    updatedAt: now,
  };
}

export function updatePlaygroundSnippet(snippet, updates = {}) {
  const now = Date.now();
  const nextTitle = updates.title != null
    ? normalizeSnippetTitle(updates.title)
    : snippet.title;

  return {
    ...snippet,
    ...updates,
    language: normalizePlaygroundLanguage(updates.language ?? snippet.language),
    title: nextTitle,
    code: updates.code ?? snippet.code,
    updatedAt: now,
  };
}

export function filterPlaygroundSnippets(snippets, query) {
  const needle = (query || '').trim().toLowerCase();
  if (!needle) return snippets;

  return snippets.filter((snippet) => {
    const haystack = [
      snippet.title,
      snippet.language,
      formatLanguageDisplay(snippet.language),
      snippet.code,
      getSnippetCodePreview(snippet.code, 200),
    ]
      .join('\n')
      .toLowerCase();

    return haystack.includes(needle);
  });
}

/** Find a snippet in a list by id. */
export function findPlaygroundSnippet(snippets, snippetId) {
  if (!snippetId) return null;
  return (snippets || []).find((item) => item.id === snippetId) || null;
}
